import { useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { getDashboardStats, getPlayerSchedule, PlayerDashboardStats, PlayerScheduleEvent } from 'API/player'
import { MembershipInfo } from 'types/auth'
import { IPlayerAssignment } from 'interfaces/player-assignment'
import { EMembershipStatus } from 'constants/membership'
import { AuthRoleIdEnum } from 'constants/user'
import { ActiveProfileType } from 'configs/constants/enums/auth'
import { teamAccentColor } from 'components/TeamCard/team-card-status-theme'
import { formatEventTime, formatEventTime24 } from 'utils/schedule'
import { useStores } from 'hooks/useStores'
import { useCoachDashboardData } from './use-coach-dashboard-data'
import { DATE_KEY } from './calendar-layout'
import { getWeekDays } from './week-utils'
import type {
  AssignedPlan,
  CalendarEvent,
  DashboardData,
  NextUpAccent,
  NextUpCardData,
  PlayerOrg,
  QuickStat,
  RallyDayPlan,
  TeamFilter,
  TeamInfo,
  WorkoutItem
} from './mock-data'

// Assembles a real DashboardData for the play dashboard. Backend does the heavy
// lifting via profile-scoped endpoints (GET /player/schedule cross-org merge +
// upcoming count, GET /player/dashboard-stats streak/completion, player
// assignment search). The FE only maps those results to widget shapes + applies
// presentation (colors, formatting). Fields with no backend source stay
// empty/zero (todos, drill metadata) — the page gates those widgets off.

const ROLE_LABEL: Record<number, string> = {
  [AuthRoleIdEnum.ADMIN]: 'Admin',
  [AuthRoleIdEnum.COACH]: 'Coach',
  [AuthRoleIdEnum.PLAYER]: 'Player',
  [AuthRoleIdEnum.OWNER]: 'Owner'
}

const NEXT_UP_ACCENTS: NextUpAccent[] = ['blue', 'orange', 'green']

// 7 inactive dots (Mon..Sun) so the streak banner renders a complete row even
// before the BE stats resolve / when the player has no completions yet.
const DEFAULT_WEEK_DOTS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'].map(label => ({ label, active: false }))

const orgInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map(w => w[0])
    .join('')
    .toUpperCase() || '?'

/** "Today · 3:30 PM" / "Wed · 3:30 PM" from an event start time. */
const nextUpTimeLabel = (event: PlayerScheduleEvent): string => {
  const time = formatEventTime(event.startTime)
  if (!event.startTime) return time
  const d = dayjs(event.startTime)
  const dayLabel = d.isSame(dayjs(), 'day') ? 'Today' : d.format('ddd')
  return time ? `${dayLabel} · ${time}` : dayLabel
}

const EMPTY_RALLY_DAY: RallyDayPlan = {
  streakDays: 0,
  weekDots: [],
  nextUpTime: '',
  routineTitle: '',
  routineSubtitle: '',
  routineDurationMin: 0,
  routineReps: 0,
  equipment: '',
  drillCount: 0,
  todayDrills: []
}

const EMPTY_DATA: DashboardData = {
  greetingName: '',
  rallyDay: EMPTY_RALLY_DAY,
  assignedPlans: [],
  orgs: [],
  team: { name: '', coach: '', level: '', nextEvent: '', nextEventTime: '', color: '#3b82f6', initials: '' },
  notifications: [],
  quickStats: [],
  teamFilters: [],
  workouts: [],
  todos: [],
  nextUpCards: [],
  calendarEvents: []
}

export interface UsePlayDashboardDataResult {
  data: DashboardData
  /** The data on hand belongs to another profile or selection than the one now chosen. */
  isLoading: boolean
  /** Coach only: the context could not be read, as opposed to being empty. */
  error: string | null
}

/** The schedule currently held, and the context + window it was read for. */
interface LoadedSchedule {
  key: string
  /** The same context without the window — what the page spinner waits on. */
  scope: string
  events: PlayerScheduleEvent[]
  upcomingCount: number
}

/**
 * @param orgId  Organization picked in the switcher; null = all of them.
 * @param teamId Team inside it; null = every Team in that scope.
 * @param isCoachScopeSettled Whether the page has finished choosing the coach's
 *   context. False until the switcher's list has resolved, so the coach's first
 *   request is the one for the organization they land on rather than a discarded
 *   all-orgs query fired before the default was applied. An athlete starts on
 *   "all" and does not wait.
 * @param range The window the calendar is showing, which is the window worth
 *   fetching. Null until the calendar has said, and then whatever its Day / Week /
 *   Month navigation lands on. Both roles honour it — a month view backed by one
 *   fixed week would be empty everywhere but that week.
 *
 * Both roles are scoped server-side by the selection. For an athlete only the
 * schedule (calendar, Next Up, Upcoming count) follows it; streak and completion
 * are the profile's own and stay whole.
 */
export const usePlayDashboardData = (
  orgId: number | null = null,
  teamId: number | null = null,
  isCoachScopeSettled = true,
  range: { from: string; to: string } | null = null
): UsePlayDashboardDataResult => {
  const { authStore, profileStore, playerAssignmentStore } = useStores()

  const [loadedSchedule, setLoadedSchedule] = useState<LoadedSchedule | null>(null)
  const [stats, setStats] = useState<PlayerDashboardStats | null>(null)
  // The profile the stats were read for; compared rather than flagged, for the
  // same one-painted-frame reason the coach hook gives.
  const [statsProfileKey, setStatsProfileKey] = useState<string | null>(null)
  // A slow answer for the previous selection must not land over the new one.
  const scheduleSeq = useRef(0)

  const userId = authStore.userDetail?.id
  const activeProfileKey = profileStore.activeProfile
    ? `${profileStore.activeProfile.type}-${profileStore.activeProfile.id}`
    : ''
  // The two roles read entirely different sources: an athlete's dashboard is
  // scoped by their profile, a coach's by the organization/Team context they
  // picked. Both hooks are called on every render — only one of them fetches.
  const isCoach = profileStore.activeProfile?.type === ActiveProfileType.COACH
  const coach = useCoachDashboardData(orgId, teamId, isCoach && isCoachScopeSettled, range)

  // The window the calendar is showing, defaulting to the current week until it
  // says otherwise.
  const week = getWeekDays()
  const from = range?.from ?? dayjs(week[0].iso).startOf('day').toISOString()
  const to = range?.to ?? dayjs(week[6].iso).endOf('day').toISOString()

  const scopeKey = `${activeProfileKey}:${orgId ?? 'all'}:${teamId ?? 'all'}`
  const scheduleKey = `${scopeKey}:${from}:${to}`

  // Streak, completion and assigned plans belong to the profile, not to the
  // selection, so they are read once per login / profile switch.
  useEffect(() => {
    if (!userId || isCoach) return
    let cancelled = false

    const load = async () => {
      const [dashStats] = await Promise.all([
        getDashboardStats().catch(() => null),
        playerAssignmentStore.fetchMyAssignments().catch(() => {})
      ])
      if (cancelled) return
      setStats(dashStats)
      setStatsProfileKey(activeProfileKey)
    }

    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, activeProfileKey, isCoach])

  // The BE resolves the player's memberships + visible events in one call, narrowed
  // to the selected organization / Team, so the FE neither fans out per org nor
  // filters afterwards. `upcomingCount` is counted from now rather than from the
  // window, so paging the calendar does not move it.
  useEffect(() => {
    if (!userId || isCoach) return
    const seq = ++scheduleSeq.current
    const scope = { organizationId: orgId ?? undefined, teamId: teamId ?? undefined }

    void getPlayerSchedule(from, to, scope)
      .catch(() => ({ events: [] as PlayerScheduleEvent[], upcomingCount: 0 }))
      .then(sched => {
        if (seq !== scheduleSeq.current) return
        setLoadedSchedule({
          key: scheduleKey,
          scope: scopeKey,
          events: sched.events,
          upcomingCount: sched.upcomingCount
        })
      })
  }, [userId, isCoach, orgId, teamId, from, to, scheduleKey, scopeKey])

  if (isCoach) return coach

  // Built every render rather than memoised: memberships and assignments are MobX
  // arrays that can change in place, which a dependency list would not notice.
  const data = buildDashboardData({
    memberships: authStore.memberships ?? [],
    assignments: playerAssignmentStore.assignments,
    schedule: loadedSchedule?.events ?? [],
    upcomingCount: loadedSchedule?.upcomingCount ?? 0,
    stats
  })

  // Compared on scope alone, like the coach hook: turning a calendar page re-reads
  // the schedule for another window, and blanking the page for that would also
  // throw away the mode and date just picked.
  const isLoading = statsProfileKey !== activeProfileKey || loadedSchedule?.scope !== scopeKey

  // An athlete's dashboard has no context to fail to read.
  return { data, isLoading, error: null }
}

interface BuildArgs {
  memberships: MembershipInfo[]
  assignments: IPlayerAssignment[]
  schedule: PlayerScheduleEvent[]
  upcomingCount: number
  stats: PlayerDashboardStats | null
}

const buildDashboardData = ({ memberships, assignments, schedule, upcomingCount, stats }: BuildArgs): DashboardData => {
  // Always build (even with zero teams/schedule/assignments) so the metric
  // widgets render their zero state instead of disappearing.
  const teams = memberships.filter(m => m.status !== EMembershipStatus.REMOVED)

  const orgById = new Map(teams.map(m => [m.orgId, m]))
  const orgLabel = (e: PlayerScheduleEvent): string => e.orgName || orgById.get(e.organizationId)?.orgName || 'Team'

  const teamFilters: TeamFilter[] = teams.map(m => ({
    id: String(m.orgId),
    name: m.orgName,
    color: teamAccentColor(m.orgId, m.orgPrimaryColor)
  }))

  const orgs: PlayerOrg[] = teams.map(m => ({
    id: String(m.orgId),
    short: orgInitials(m.orgName),
    name: m.orgName,
    role: ROLE_LABEL[m.roleId] ?? 'Member',
    color: teamAccentColor(m.orgId, m.orgPrimaryColor)
  }))

  const workouts: WorkoutItem[] = schedule.map(e => ({
    id: String(e.id),
    title: e.name || 'Practice',
    startTime: formatEventTime(e.startTime),
    endTime: formatEventTime(e.endTime),
    location: e.location || '',
    orgId: String(e.organizationId),
    date: e.scheduledDate ? dayjs(e.scheduledDate).format('YYYY-MM-DD') : ''
  }))

  const calendarEvents: CalendarEvent[] = schedule.map(e => ({
    id: String(e.id),
    // From `startTime`, not `scheduledDate`: the latter is stored as local
    // midnight, so for a viewer in another timezone the two can fall on different
    // days and the card would be filed under one and drawn at the other's time.
    date: dayjs(e.startTime || e.scheduledDate).format(DATE_KEY),
    start: formatEventTime24(e.startTime),
    end: formatEventTime24(e.endTime),
    title: e.name || 'Practice',
    location: e.location || '',
    orgId: String(e.organizationId),
    teamTag: {
      label: orgLabel(e),
      // Schedule rows carry no branding — read it off the membership for that org.
      color: teamAccentColor(e.organizationId, orgById.get(e.organizationId)?.orgPrimaryColor)
    }
  }))

  const assignedPlans: AssignedPlan[] = assignments.map(a => ({
    id: String(a._id),
    title: a.name,
    org: a.orgName?.trim() || (orgById.size === 1 ? teams[0].orgName : ''),
    weekProgress: a.currentWeekNumber ?? 0,
    weekTotal: a.weeks?.length ?? 0,
    status: a.progressPercent >= 100 ? 'ahead' : a.progressPercent > 0 ? 'on-track' : 'behind'
  }))

  // Featured "Next Up" = earliest event still ahead of now (schedule is already
  // BE-sorted; filtering future preserves order).
  const nextEvent = schedule.find(e => e.startTime && dayjs(e.startTime).isAfter(dayjs()))
  const upcoming = schedule.filter(e => e.startTime && dayjs(e.startTime).isAfter(dayjs()))

  const streakDays = stats?.streakDays ?? 0
  const weekDots = stats?.weekDots?.length ? stats.weekDots : DEFAULT_WEEK_DOTS
  const rallyDay: RallyDayPlan = nextEvent
    ? {
        ...EMPTY_RALLY_DAY,
        streakDays,
        weekDots,
        nextUpTime: nextUpTimeLabel(nextEvent),
        routineTitle: nextEvent.name || 'Practice',
        routineSubtitle: orgLabel(nextEvent),
        routineDurationMin: nextEvent.duration ?? 0,
        // No equipment field on practice events; surface the venue instead of a
        // fabricated equipment list.
        equipment: nextEvent.location ?? ''
      }
    : { ...EMPTY_RALLY_DAY, streakDays, weekDots }

  const nextUpCards: NextUpCardData[] = upcoming.slice(0, 6).map((e, i) => {
    const meta = [e.duration ? `${e.duration} MIN` : '', e.location || ''].filter(Boolean)
    return {
      id: String(e.id),
      tag: orgLabel(e).toUpperCase(),
      title: e.name || 'Practice',
      byline: nextUpTimeLabel(e),
      meta,
      ctaLabel: 'View details',
      accent: NEXT_UP_ACCENTS[i % NEXT_UP_ACCENTS.length]
    }
  })

  // Quick stats — only tiles with a real source. upcoming count + weekly
  // completion come from the BE; training-hours has no source and is omitted.
  const activeTeams = teams.filter(m => (m.status ? m.status === EMembershipStatus.ACTIVE : true))
  // Completion = overall completed/assigned drills. Trend = week-over-week change
  // in drills completed, shown as a percentage ("+8% last week"). The BE exposes no
  // weekly completion-rate, so this is a volume delta vs last week; with no
  // completions last week there's no baseline to divide by, so fall back to text.
  const completionPercent = stats?.completionPercent ?? 0
  const thisWeekDrills = stats?.weeklyCompletedCount ?? 0
  const prevWeekDrills = stats?.previousWeekCompletedCount ?? 0
  const weekOverWeekPct =
    prevWeekDrills > 0 ? Math.round(((thisWeekDrills - prevWeekDrills) / prevWeekDrills) * 100) : null
  const completionDeltaLabel =
    weekOverWeekPct !== null
      ? `${weekOverWeekPct >= 0 ? '+' : '−'}${Math.abs(weekOverWeekPct)}% last week`
      : thisWeekDrills > 0
        ? 'New this week'
        : 'No activity yet'

  const quickStats: QuickStat[] = [
    {
      // No backend source confirmed yet — shown as TBD (per HF-1444: mark
      // un-scoped widgets TBD instead of hiding them).
      id: 'training',
      value: 'TBD',
      label: 'TRAINING HRS',
      delta: 'Coming soon',
      iconKey: 'training',
      accent: '#63E3F9',
      tbd: true
    },
    {
      id: 'teams',
      value: String(activeTeams.length),
      label: 'ACTIVE TEAMS',
      delta:
        activeTeams
          .slice(0, 2)
          .map(m => m.orgName)
          .join(', ') || 'No teams yet',
      iconKey: 'teams',
      accent: '#FFA500'
    },
    {
      id: 'upcoming',
      value: String(upcomingCount),
      label: 'UPCOMING',
      delta: 'This week',
      iconKey: 'upcoming',
      accent: '#F687B3'
    },
    {
      id: 'completion',
      value: `${completionPercent}%`,
      label: 'COMPLETION',
      delta: completionDeltaLabel,
      iconKey: 'completion',
      accent: '#A8E46E'
    }
  ]

  const firstTeam = teams[0]
  const team: TeamInfo = firstTeam
    ? {
        name: firstTeam.orgName,
        coach: '',
        level: '',
        nextEvent: nextEvent?.name ?? '',
        nextEventTime: nextEvent ? nextUpTimeLabel(nextEvent) : '',
        color: teamAccentColor(firstTeam.orgId, firstTeam.orgPrimaryColor),
        initials: orgInitials(firstTeam.orgName)
      }
    : EMPTY_DATA.team

  return {
    greetingName: '',
    rallyDay,
    assignedPlans,
    orgs,
    team,
    // Notifications are owned by the bell (notificationStore); todos have no
    // backend source. Both intentionally empty here.
    notifications: [],
    quickStats,
    teamFilters,
    workouts,
    todos: [],
    nextUpCards,
    calendarEvents
  }
}
